import { useCallback, useState } from 'react';

import { uuid } from '@utils/uuid';

export type PendingFile = {
  id: string;
  file: File;
};

type UseFileUploadOptions = {
  allowedExtensions: string[];
  getErrorMessage?: (extension: string) => string;
};

export const useFileUpload = ({ allowedExtensions, getErrorMessage }: UseFileUploadOptions) => {
  const [uploadedFiles, setUploadedFiles] = useState<PendingFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelected = useCallback(
    (file: File) => {
      if (!file) return;

      setFileError(null);

      const match = /\.([^.]+)$/.exec(file.name);
      const extension = match ? `.${match[1].toLowerCase()}` : '';

      if (!allowedExtensions.includes(extension)) {
        const errorMessage = getErrorMessage
          ? getErrorMessage(extension)
          : `File type ${extension} is not supported.`;
        setFileError(errorMessage);
        return;
      }

      setUploadedFiles((currentFiles) => {
        const hasDuplicate = currentFiles.some(
          ({ file: existing }) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        );

        if (hasDuplicate) {
          return currentFiles;
        }

        return [...currentFiles, { id: uuid(), file }];
      });
    },
    [allowedExtensions, getErrorMessage]
  );

  const handleRemoveFile = useCallback((fileId: string) => {
    setUploadedFiles((currentFiles) => currentFiles.filter((item) => item.id !== fileId));
  }, []);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
    setFileError(null);
  }, []);

  const clearError = useCallback(() => {
    setFileError(null);
  }, []);

  const setError = useCallback((error: string) => {
    setFileError(error);
  }, []);

  return {
    uploadedFiles,
    fileError,
    handleFileSelected,
    handleRemoveFile,
    clearFiles,
    clearError,
    setError,
  };
};
